import { Box, Checkbox, FormControlLabel, FormHelperText, Stack, Typography, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { get, isEmpty, pickBy, some } from 'lodash';
import { useMemo, useState } from 'react';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';

import { useMutation } from '@tanstack/react-query';
import { HttpStatusCode } from 'axios';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import CreditCostIndicator from 'src/components/common/CreditCostIndicator';
import DatePickerWithDefaultIcon from 'src/components/DatePicker';
import FormItemCountry from 'src/components/Form/FormItemCountry';
import FormSearchHsCode from 'src/components/Form/FormSearchHsCode';
import ModalComponent from 'src/components/Modal/ModalComponent';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { useNotification } from 'src/hooks/useNotification';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { ReportService } from 'src/services/api/report';
import { useQueryUsageInformation } from 'src/services/queries';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { useQueryListHSCodeAIReport, useQueryTransactionsCount } from 'src/services/queries/useQueryTradeData';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST_BILLS_OF_LADING } from 'src/utils/constants/config';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { getTradeDataEndMonth, getTradeDataStartMonth } from 'src/utils/helpers/dayjs';
import { formatNumberWithSuffix } from 'src/utils/helpers/number';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import { getCountryByCountryCode } from 'src/utils/helpers/stringHelpers';
import { TypeGlobalTradeDataFormSearch } from 'src/utils/typings/dto/tradeData';
import * as Yup from 'yup';
import { FeatureAction } from 'src/utils/constants/featuresPoint';

interface IProps {
  open: boolean;
  handleClose: () => void;
  onSuccess: () => void;
}

export enum TradeDataSearchBy {
  HS_CODE = 'hs_code',
  HS_CODE_AI = 'hs_code_ai',
}

const FormSearchGlobalTrade = (props: IProps) => {
  const { handleClose, open, onSuccess } = props;
  const theme = useTheme();
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();
  const params = useQueryParams();
  const { mutateAsync, isPending } = useMutation({ mutationFn: ReportService.createTradeDataReport });
  const { notification } = useNotification();
  // const { supplierBuyerCountrySortParams } = useTradeDataSortStore();
  const { data: dataUsage } = useQueryUsageInformation();

  const { t } = useTranslation();

  const getDefaultValue = (key: string) => {
    return get(params, key, '') as string;
  };
  const { getFeaturePoints } = useFeatureAccess();

  const pointsExportTradeData = getFeaturePoints(URL_ENDPOINT.myReport.createTradeDataReport, FeatureAction.VIEW);
  // const pointsExportTradeData = useGetPointsFeature(LIST_FEATURES.REPORT_TRADE_DATA, LIST_ACTIONS.VIEW, dataUsage);

  const [initTradeFrom, initTradeTo] = useMemo(() => {
    return [
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD'),
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD'),
    ];
  }, [runtimeConfig.DEFAULT_TRADE_DATA_FROM, runtimeConfig.DEFAULT_TRADE_DATA_TO]);

  const formik = useFormik<TypeGlobalTradeDataFormSearch>({
    enableReinitialize: true,
    initialValues: {
      hs_code: null,
      import_country: getCountryByCountryCode(getDefaultValue('imp_country_code')),
      export_country: getCountryByCountryCode(getDefaultValue('exp_country_code')),
      port_of_origin: null,
      port_of_delivery: null,
      supplier: undefined,
      buyer: undefined,
      trade_from: getDefaultValue('start_date') ? dayjs(getDefaultValue('start_date'), 'YYYYMMDD') : initTradeFrom,
      trade_to: getDefaultValue('end_date') ? dayjs(getDefaultValue('end_date'), 'YYYYMMDD') : initTradeTo,
      ai_type: runtimeConfig?.DEFAULT_MODEL_AI_TYPE,
      buyer_country_selected: getDefaultValue('buyer_country_selected'),
      is_only_global: getDefaultValue('is_only_global') == 'true',
    },
    initialTouched: {
      trade_from: true,
      trade_to: true,
    },
    validateOnChange: true,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      trade_from: Yup.mixed()
        .required('Trade Date From is required')
        .test('is-greater', t('trade_data.common.field.error.date_range.invalid'), function (_, context) {
          const trade_from = dayjs(context.parent.trade_from || 'Invalid date');
          const trade_to = dayjs(context.parent.trade_to || 'Invalid date');
          if (!(trade_from.isValid() && trade_to.isValid())) return true;
          return trade_from.isBefore(trade_to);
        }),
      trade_to: Yup.mixed().required('Trade Date To is required'),
      export_country: Yup.mixed()
        .nullable()
        .test('not-duplicated', 'Export and import countries must be different', (_, context) => {
          const export_country = context.parent.export_country?.code;
          const import_country = context.parent.import_country?.code;

          if (!(export_country && import_country)) return true;

          return export_country !== import_country;
        }),
      buyer_country_selected: Yup.mixed()
        .nullable()
        .test('is-required', 'Import Country is required', (_, context) => {
          const is_only_global = context.parent.is_only_global;
          const buyer_country_selected = context.parent.buyer_country_selected;

          if (is_only_global) return true;

          return !isEmpty(buyer_country_selected);
        }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const searchString = handleQueryParamFromObject(
        pickBy(bolRequestPayload, (value: ExpectedAny) => !(value === undefined || value === null)),
      );

      const res = await mutateAsync({
        hs_code: values.hs_code?.code,
        start_date: dayjs(values.trade_from).format(FORMAT_DATE_TIME.yyyy_mm_dd),
        end_date: dayjs(values.trade_to).format(FORMAT_DATE_TIME.yyyy_mm_dd),
        imp_country_code: values.buyer_country_selected,
        is_only_global: values.is_only_global,
      });

      if (res.status_code === HttpStatusCode.Created) onSuccess();
      notification(
        res,
        {
          title: 'Trade Data Report Request Submitted',
          description: `Your request has been submitted successfully and is waiting for ${res.data.waiting} other requests to be processed.`,
        },
        {
          title: 'Failed to Create Trade Data Report',
          description:
            'There was an error while creating the trade data report. Please try again or contact support if the issue persists.',
        },
      );
      handleClose();
      setSubmitting(false);
    },
  });
  const [hsCodeSearchKeyword, setHSCodeSearchKeyword] = useState('');
  const bolRequestPayload = useMemo(() => {
    const values = formik.values;
    return {
      hs_code: values.hs_code?.code,
      start_date: getTradeDataStartMonth(values.trade_from)?.format(FORMAT_DATE_TIME.yyyy_mm_dd),
      end_date: getTradeDataEndMonth(values?.trade_to).format(FORMAT_DATE_TIME.yyyy_mm_dd),
      offset: 0,
      limit: LIMIT_GET_LIST_BILLS_OF_LADING,
      exp_country_code: values.export_country?.code,
      imp_country_code: values?.is_only_global ? values?.buyer_country_selected : '',
      exp_port_code: values.port_of_origin?.port_code,
      imp_port_code: values.port_of_delivery?.port_code,
      supplier_id: values.supplier?.id || '',
      buyer_id: values.buyer?.id || '',
      ai_type: runtimeConfig?.DEFAULT_MODEL_AI_TYPE,
      buyer_country_selected: values?.buyer_country_selected,
      is_only_global: values?.is_only_global,
    };
  }, [formik.values]);

  // const buyerCountryRequestPayload = useMemo<IGetSupplierBuyerCountryRequestPayload>(() => {
  //   const values = formik.values;
  //   return {
  //     limit: 25,
  //     start_date: getTradeDataStartMonth(values.trade_from)?.format(FORMAT_DATE_TIME.yyyy_mm_dd),
  //     end_date: getTradeDataEndMonth(values?.trade_to).format(FORMAT_DATE_TIME.yyyy_mm_dd),
  //     hs_code: values.hs_code?.code || '',
  //     is_buyer: true,
  //     sort_by: supplierBuyerCountrySortParams?.sortBy,
  //     order: supplierBuyerCountrySortParams?.sort,
  //     ai_type: values?.ai_type,
  //     buyer_country_selected: values?.buyer_country_selected,
  //   };
  // }, [formik.values, supplierBuyerCountrySortParams]);

  const transactionsCountResp = useQueryTransactionsCount(
    formik.isValid && !formik.isValidating ? bolRequestPayload : null,
  );
  const dataHSCodeOptionsResp = useQueryListHSCodeAIReport({
    country_code: formik.values.import_country?.code,
    keyword: hsCodeSearchKeyword || '',
    limit: 50,
  });

  const getError = (id: string) => {
    return get(formik.touched, id) && Boolean(get(formik.errors, id));
  };

  const renderError = (id: string) => {
    return (
      get(formik.touched, id) &&
      get(formik.errors, id) && (
        <FormHelperText sx={{ marginLeft: 2 }} error id="standard-weight-helper-text-email-login">
          {get(formik.errors, id, '').toString()}
        </FormHelperText>
      )
    );
  };

  return (
    <ModalComponent padding={4} handleClose={handleClose} open={open}>
      <Stack flexDirection={'column'} justifyContent={'center'} alignItems={'center'} gap={1}>
        <Box width={'fit-content'}>
          <Typography textAlign={'center'} variant="h5">
            Import/Export Data
          </Typography>
          <Box sx={{ mt: 1 }} />
        </Box>
        <FormikForm formik={formik}>
          <Box width={600}>
            <Typography mb={4} textAlign={'center'} color={theme.palette.text.secondary} variant="body1">
              {t('Explore import export data using HS code or product name.')}{' '}
            </Typography>

            <Box sx={{ mt: 4 }} />
            <Stack direction={'column'} spacing={2}>
              <Box>
                <FormSearchHsCode
                  required={true}
                  id={'hs_code'}
                  onBlur={() => {
                    formik.setFieldTouched('hs_code');
                  }}
                  inputValue={hsCodeSearchKeyword || ''}
                  value={formik.values.hs_code}
                  options={dataHSCodeOptionsResp.data?.data}
                  onChange={(value: ExpectedAny) => {
                    // setHSCodeValue(value)
                    formik.setFieldValue('hs_code', value);
                  }}
                  onTextInputChange={(value: string) => {
                    setHSCodeSearchKeyword(value);
                  }}
                  loading={dataHSCodeOptionsResp.isFetching}
                  isError={getError('hs_code')}
                />
                {renderError('hs_code')}
              </Box>
              <Stack direction={'row'} spacing={2}>
                <Box flex={1}>
                  <DatePickerWithDefaultIcon
                    disableFuture={true}
                    value={formik.values.trade_from}
                    minDate={dayjs(runtimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                    maxDate={dayjs(runtimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                    // disabled={true}
                    onChange={(data) => {
                      formik.setFieldValue('trade_from', data);
                      formik.setFieldTouched('trade_from');
                    }}
                    label={t('trade_data.search_form.input.trade_from')}
                    slotProps={{
                      textField: {
                        error: !!formik.errors.trade_from,
                        size: 'small',
                        fullWidth: true,
                        required: true,
                      },
                    }}
                    views={['month', 'year']}
                  />
                  {renderError('trade_from')}
                </Box>
                <Box flex={1}>
                  <DatePickerWithDefaultIcon
                    disableFuture={true}
                    value={formik.values.trade_to}
                    // disabled={true}
                    minDate={dayjs(runtimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                    maxDate={dayjs(runtimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                    onChange={(data) => {
                      formik.setFieldValue('trade_to', data);
                      formik.setFieldTouched('trade_to');
                    }}
                    label={t('trade_data.search_form.input.trade_to')}
                    slotProps={{
                      textField: {
                        error: !!formik.errors.trade_from,
                        size: 'small',
                        fullWidth: true,
                        required: true,
                      },
                    }}
                    views={['month', 'year']}
                  />
                  {renderError('trade_to')}
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box width="100%">
                  <FormItemCountry
                    id="buyer_country_selected"
                    label="Import Country"
                    disabled={formik.values.is_only_global}
                    required
                  />
                  {renderError('buyer_country_selected')}
                </Box>
                <FormControlLabel
                  value={formik.values.is_only_global}
                  key="is_only_global"
                  name="is_only_global"
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue('buyer_country_selected', '');
                  }}
                  onBlur={formik.handleBlur}
                  control={<Checkbox checked={formik.values.is_only_global} />}
                  label={<Typography variant="body2">Only Global Report</Typography>}
                />
              </Stack>
              {transactionsCountResp.data?.data !== undefined && (
                <Stack
                  flexDirection={'row'}
                  gap={2}
                  alignItems={'center'}
                  sx={{
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                    background: theme.palette.success.light,
                  }}
                >
                  <>
                    <Typography variant={'body2'}>
                      We&apos; ve found{' '}
                      <Typography color={theme.palette.info.main} variant={'body2'} display={'inline'}>
                        {formatNumberWithSuffix(transactionsCountResp.data?.data)} Bills of Lading
                      </Typography>{' '}
                      matching your search criteria.
                    </Typography>
                  </>
                </Stack>
              )}
              <ButtonWithLoading
                sx={{ height: 53 }}
                size="large"
                variant="contained"
                isRounded
                isLoading={some([transactionsCountResp.isFetching, isPending])}
                type="submit"
                disabled={transactionsCountResp.data?.data === 0}
              >
                <Stack alignItems={'center'} direction={'row'} justifyContent="center" gap={1}>
                  <CreditCostIndicator pointData={pointsExportTradeData} />
                  Create trade data report
                </Stack>
              </ButtonWithLoading>
              {transactionsCountResp.isFetching && (
                <Typography align="center" variant="caption2" color="text.secondary">
                  Please wait a few seconds while we compile your report.
                </Typography>
              )}
            </Stack>
          </Box>
        </FormikForm>
      </Stack>
    </ModalComponent>
  );
};

export default FormSearchGlobalTrade;
